/**
 * Bannière de consentement aux cookies.
 *
 * S'appuie sur l'API Shopify Customer Privacy pour un consentement réel et
 * conforme (RGPD). Si l'API n'est pas disponible (région sans exigence de
 * consentement, API désactivée), la bannière reste utilisable et mémorise le
 * choix de l'utilisateur dans localStorage afin de ne plus le solliciter.
 *
 * @see https://shopify.dev/docs/api/customer-privacy
 */

const STORAGE_KEY = 'gace_cookie_consent';
const CONSENT_CATEGORIES = ['analytics', 'marketing', 'preferences', 'sale_of_data'];

class CookieBanner extends HTMLElement {
  constructor() {
    super();
    this.customerPrivacy = null;
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.mainView = this.querySelector('[data-cookie-view="main"]');
    this.preferencesView = this.querySelector('[data-cookie-view="preferences"]');
    this.toggles = {};

    CONSENT_CATEGORIES.forEach((category) => {
      const input = this.querySelector(`[data-cookie-toggle="${category}"]`);
      if (input) this.toggles[category] = input;
    });

    this.addEventListener('click', this.onClick);
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  init() {
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (error) {
            this.initFallback();
            return;
          }

          this.customerPrivacy = window.Shopify.customerPrivacy || null;

          if (!this.customerPrivacy) {
            this.initFallback();
            return;
          }

          try {
            if (this.shouldDisplay()) {
              this.show();
            }
          } catch (err) {
            this.initFallback();
          }
        }
      );
    } else {
      this.initFallback();
    }
  }

  /** Sans API de consentement : on se base sur le choix stocké localement. */
  initFallback() {
    if (!this.getStoredConsent()) {
      this.show();
    }
  }

  /**
   * Détermine si la bannière doit s'afficher via l'API Customer Privacy.
   *
   * shouldShowBanner() peut renvoyer true même après un choix enregistré : on
   * vérifie donc d'abord si le visiteur a déjà tranché (consentement non vide).
   */
  shouldDisplay() {
    if (this.getStoredConsent()) {
      return false;
    }
    if (this.hasDecision(this.customerPrivacy.currentVisitorConsent())) {
      return false;
    }
    return this.customerPrivacy.shouldShowBanner();
  }

  /** Vrai si l'utilisateur a déjà accepté ou refusé au moins une catégorie. */
  hasDecision(consent) {
    if (!consent) return false;
    return CONSENT_CATEGORIES.some((category) => {
      const value = consent[category];
      return value === 'yes' || value === 'no';
    });
  }

  onClick(event) {
    const trigger = event.target.closest('[data-cookie-action]');
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();
    const action = trigger.getAttribute('data-cookie-action');

    switch (action) {
      case 'accept':
        this.setConsent(this.buildConsent(true));
        break;
      case 'decline':
        this.setConsent(this.buildConsent(false));
        break;
      case 'preferences':
        this.showPreferences();
        break;
      case 'save':
        this.setConsent(this.readToggles());
        break;
      default:
        break;
    }
  }

  /** Construit un objet de consentement avec la même valeur pour toutes les catégories. */
  buildConsent(value) {
    return CONSENT_CATEGORIES.reduce((consent, category) => {
      consent[category] = value;
      return consent;
    }, {});
  }

  /** Lit l'état des interrupteurs du panneau "Mes préférences". */
  readToggles() {
    return CONSENT_CATEGORIES.reduce((consent, category) => {
      const toggle = this.toggles[category];
      consent[category] = toggle ? toggle.checked : false;
      return consent;
    }, {});
  }

  setConsent(consent) {
    this.persistConsent(consent);

    if (this.customerPrivacy && typeof this.customerPrivacy.setTrackingConsent === 'function') {
      this.customerPrivacy.setTrackingConsent(consent, () => this.hide());
    } else {
      this.hide();
    }

    this.dispatchEvent(
      new CustomEvent('cookie-consent:updated', { bubbles: true, detail: { consent } })
    );
  }

  persistConsent(consent) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    } catch (err) {
      /* localStorage indisponible (mode privé) : on ignore silencieusement. */
    }
  }

  getStoredConsent() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  }

  show() {
    this.hidden = false;
  }

  hide() {
    this.hidden = true;
  }

  showPreferences() {
    if (this.mainView) this.mainView.hidden = true;
    if (this.preferencesView) this.preferencesView.hidden = false;
  }
}

if (!customElements.get('cookie-banner')) {
  customElements.define('cookie-banner', CookieBanner);
}
